import { useTranslation } from "react-i18next";
import { type BreadcrumbProps, ConfigProvider, Layout, Space, type ThemeConfig } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { Image } from "@av/components/atoms/Image";
import {
  HeaderBreadcrumb,
  type IgnoredBreadcrumbSegments,
} from "./nav/header-breadcrumb/HeaderBreadcrumb";
import { ResponsiveNav } from "./nav/ResponsiveNav";
import { UserAvatar } from "../molecules/EmployeeAvatar";

import type { NavItem } from "./nav/types";
import type { AppSwitcherEntry, DisplayUser, HeaderAdminSettingsControls } from "./type";

const useStyles = createStyles(({ token, css }) => ({
  header: css`
    background: transparent;
    margin: ${token.margin}px;
    padding: 0;
    height: 51.05px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
}));

type ResponsiveHeaderProps = {
  apps: AppSwitcherEntry[];
  currentApp: string;
  onAppChange: (path: string) => void;
  navItems: NavItem[];
  secondaryBreadcrumbLabel?: React.ReactNode;
  ignoredSegmentsInBreadcrumb?: IgnoredBreadcrumbSegments;
  user?: DisplayUser;
  isDemo: boolean;
  adminSettingsControls?: HeaderAdminSettingsControls;
  hideBreadcrumb?: boolean;
  permissions: Array<string>;
  breadcrumbItems?: BreadcrumbProps["items"];
  navTheme?: ThemeConfig;
};

function ResponsiveHeader(props: ResponsiveHeaderProps) {
  const {
    apps,
    currentApp,
    onAppChange,
    navItems,
    user,
    isDemo,
    permissions,
    adminSettingsControls,
    hideBreadcrumb,
    secondaryBreadcrumbLabel,
    ignoredSegmentsInBreadcrumb,
    breadcrumbItems,
    navTheme,
  } = props;
  const { t } = useTranslation();
  const { Header } = Layout;
  const { styles } = useStyles();

  return (
    <Header className={styles.header}>
      <ConfigProvider theme={navTheme}>
        <ResponsiveNav onDarkModeToggle={() => {}} navItems={navItems} />
      </ConfigProvider>
      <Space align="center">
        <HeaderBreadcrumb
          onAppChange={onAppChange}
          apps={apps}
          company={user?.company}
          currentApp={currentApp}
          navItems={navItems}
          hideBreadcrumb={hideBreadcrumb}
          secondaryBreadcrumbLabel={secondaryBreadcrumbLabel}
          ignoredSegmentsInBreadcrumb={ignoredSegmentsInBreadcrumb}
          items={breadcrumbItems}
        />
      </Space>
      <Space align="center">
        {adminSettingsControls?.isAdminSettingsVisible &&
          adminSettingsControls?.hasAdminSettingsPermission(permissions) && (
            <Button
              testIdPrefix="admin-settings-nav"
              type="text"
              shape="circle"
              icon={
                <Image
                  testIdPrefix="admin-settings-nav-icon"
                  src="/admin-settings/admin_panel_settings.svg"
                  preview={false}
                />
              }
              aria-label={t("platform.adminSettings.navButton")}
              onClick={adminSettingsControls.openAdminSettings}
            />
          )}
        <UserAvatar
          isDemo={isDemo}
          testIdPrefix="header-profile-dropdown"
          info={user}
          size="small"
        />
      </Space>
    </Header>
  );
}

export { ResponsiveHeader };
